export const ANIMAL_PACKAGE_CODE = 'SEOUL25'
export const ANIMAL_PACKAGE_REQUIRED_AREA_M2 = 500000
export const ANIMAL_PACKAGE_CODE_PATTERN = /^[A-Z0-9_]{1,50}$/

export const normalizeAnimalPackageCode = (value = '') => String(value).trim().toUpperCase()

export const isValidAnimalPackageCode = (value = '') => (
    ANIMAL_PACKAGE_CODE_PATTERN.test(normalizeAnimalPackageCode(value))
)

export const SEOUL25_REGIONS = [
    { regionCode: 'SEOUL25_GANGNAM', regionName: '강남구' },
    { regionCode: 'SEOUL25_GANGDONG', regionName: '강동구' },
    { regionCode: 'SEOUL25_GANGBUK', regionName: '강북구' },
    { regionCode: 'SEOUL25_GANGSEO', regionName: '강서구' },
    { regionCode: 'SEOUL25_GWANAK', regionName: '관악구' },
    { regionCode: 'SEOUL25_GWANGJIN', regionName: '광진구' },
    { regionCode: 'SEOUL25_GURO', regionName: '구로구' },
    { regionCode: 'SEOUL25_GEUMCHEON', regionName: '금천구' },
    { regionCode: 'SEOUL25_NOWON', regionName: '노원구' },
    { regionCode: 'SEOUL25_DOBONG', regionName: '도봉구' },
    { regionCode: 'SEOUL25_DONGDAEMUN', regionName: '동대문구' },
    { regionCode: 'SEOUL25_DONGJAK', regionName: '동작구' },
    { regionCode: 'SEOUL25_MAPO', regionName: '마포구' },
    { regionCode: 'SEOUL25_SEODAEMUN', regionName: '서대문구' },
    { regionCode: 'SEOUL25_SEOCHO', regionName: '서초구' },
    { regionCode: 'SEOUL25_SEONGDONG', regionName: '성동구' },
    { regionCode: 'SEOUL25_SEONGBUK', regionName: '성북구' },
    { regionCode: 'SEOUL25_SONGPA', regionName: '송파구' },
    { regionCode: 'SEOUL25_YANGCHEON', regionName: '양천구' },
    { regionCode: 'SEOUL25_YEONGDEUNGPO', regionName: '영등포구' },
    { regionCode: 'SEOUL25_YONGSAN', regionName: '용산구' },
    { regionCode: 'SEOUL25_EUNPYEONG', regionName: '은평구' },
    { regionCode: 'SEOUL25_JONGNO', regionName: '종로구' },
    { regionCode: 'SEOUL25_JUNG', regionName: '중구' },
    { regionCode: 'SEOUL25_JUNGNANG', regionName: '중랑구' },
]

const SEOUL25_REGION_ORDER_BY_NAME = new Map(
    SEOUL25_REGIONS.map((region, index) => [region.regionName, index])
)

export const createSeoul25CategoryRows = ({ price = 30000, quantity = 300 } = {}) => (
    SEOUL25_REGIONS.map((region, index) => ({
        id: index,
        type: 2,
        distance: 0,
        price,
        name: region.regionName,
        quantity,
        status: 0,
        order: index,
        webVisible: 1,
        appVisible: 1,
    }))
)

export const sortSeoul25CategoryRows = (categoryRows = []) => {
    if (!Array.isArray(categoryRows)) {
        return []
    }

    return [...categoryRows].sort((a, b) => {
        const aName = String(a.name || '').trim()
        const bName = String(b.name || '').trim()
        const aOrder = SEOUL25_REGION_ORDER_BY_NAME.get(aName)
        const bOrder = SEOUL25_REGION_ORDER_BY_NAME.get(bName)

        if (aOrder !== undefined && bOrder !== undefined) {
            return aOrder - bOrder
        }

        if (aOrder !== undefined) {
            return -1
        }

        if (bOrder !== undefined) {
            return 1
        }

        return Number(a.order ?? a.id ?? 0) - Number(b.order ?? b.id ?? 0)
    }).map((category, index) => ({
        ...category,
        order: index,
    }))
}

export const isSeoul25CategoryRows = (categoryRows = []) => {
    const activeRows = Array.isArray(categoryRows)
        ? categoryRows.filter((category) => Number(category.status || 0) === 0)
        : []

    if (activeRows.length !== SEOUL25_REGIONS.length) {
        return false
    }

    const categoryNames = new Set(
        activeRows.map((category) => String(category.name || '').trim()).filter(Boolean)
    )

    return SEOUL25_REGIONS.every((region) => categoryNames.has(region.regionName))
}

export const isAnimalPackageDraftPayload = (draft = null) => {
    if (!draft || typeof draft !== 'object') {
        return false
    }

    return draft.data?.isAnimalPackage === true || isSeoul25CategoryRows(draft.arr)
}

export const createDefaultAnimalPackageState = () => ({
    isAnimalPackage: false,
    packageCode: ANIMAL_PACKAGE_CODE,
    progressPackageCode: '',
    requiredAreaM2: ANIMAL_PACKAGE_REQUIRED_AREA_M2,
})

export const normalizeAnimalPackageStateFromDetail = (data = {}) => {
    const animalPackage = data.animal_package

    if (!animalPackage) {
        return createDefaultAnimalPackageState()
    }

    return {
        isAnimalPackage: true,
        packageCode: animalPackage.packageCode || ANIMAL_PACKAGE_CODE,
        progressPackageCode: animalPackage.progress_animal_package?.packageCode || '',
        requiredAreaM2: animalPackage.requiredAreaM2 || ANIMAL_PACKAGE_REQUIRED_AREA_M2,
    }
}

export const buildAnimalPackageRegions = (categoryRows = []) => {
    const categoryByRegionName = new Map(
        categoryRows
            .filter((category) => Number(category.status || 0) === 0)
            .map((category) => [String(category.name || '').trim(), category])
    )

    return SEOUL25_REGIONS.map((region, index) => {
        const category = categoryByRegionName.get(region.regionName)
        const productCategoryId = Number(category?.productCategoryId || 0)

        return {
            productCategoryId,
            regionCode: region.regionCode,
            regionName: region.regionName,
            sortNo: index + 1,
        }
    })
}

export const validateAnimalPackageRegions = (regions = []) => (
    regions.every((region) => Number(region.productCategoryId) > 0)
)
